import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import { errorMessage, readJson } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authFetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await readJson(res);
        setError(errorMessage(res, data));
        return;
      }
      setSent(true);
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .fp-input {
          width: 100%;
          background: #111;
          border: 1px solid rgba(255,255,255,0.1);
          color: #e5e5e5;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          padding: 12px 14px;
          outline: none;
          transition: border-color 0.18s;
          box-sizing: border-box;
        }
        .fp-input:focus { border-color: rgba(232,160,32,0.6); }
        .fp-input::placeholder { color: #4a4a4a; }
        .fp-btn {
          width: 100%;
          background: #E8A020;
          color: #000;
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          font-size: 0.72rem;
          padding: 14px;
          border: none;
          cursor: pointer;
          transition: background 0.18s, box-shadow 0.18s;
          box-shadow: 0 0 24px rgba(232,160,32,0.3);
        }
        .fp-btn:hover:not(:disabled) { background: #f0b030; box-shadow: 0 0 40px rgba(232,160,32,0.55); }
        .fp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .fp-back {
          background: none;
          border: none;
          color: #606060;
          font-family: 'Courier Prime', monospace;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          cursor: pointer;
          transition: color 0.18s;
          padding: 0;
        }
        .fp-back:hover { color: #E8A020; }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(232,160,32,0.06) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '400px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <button className="fp-back" onClick={() => navigate('/login')} style={{ marginBottom: '24px', display: 'inline-block' }}>
              ← Back to Login
            </button>
            <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.4rem', color: '#E8A020', letterSpacing: '0.14em' }}>
              PANGAJI
            </div>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.3em', color: '#606060', marginTop: '6px' }}>
              The Story Engine
            </p>
          </div>

          <div style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.07)', padding: '40px 36px' }}>
            {sent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Courier Prime, monospace', fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#E8A020', marginBottom: '16px' }}>
                  Check your inbox
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#808080', lineHeight: 1.7, marginBottom: '24px' }}>
                  If <strong style={{ color: '#b0b0b0' }}>{email}</strong> is registered, we've sent a password reset link. It expires in 1 hour.
                </p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#505050', lineHeight: 1.6 }}>
                  Didn't receive it? Check your spam folder, or{' '}
                  <button
                    onClick={() => { setSent(false); setError(''); }}
                    style={{ background: 'none', border: 'none', color: '#E8A020', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}
                  >
                    try again
                  </button>.
                </p>
              </div>
            ) : (
              <>
                <h1 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px', color: '#fff' }}>
                  Forgot password?
                </h1>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#606060', marginBottom: '24px', lineHeight: 1.6 }}>
                  Enter your account email and we'll send you a reset link.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080', marginBottom: '6px' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      className="fp-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#f87171', margin: 0, lineHeight: 1.5 }}>
                      {error}
                    </p>
                  )}

                  <button type="submit" className="fp-btn" disabled={loading} style={{ marginTop: '8px' }}>
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p style={{ textAlign: 'center', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', color: '#404040', marginTop: '24px', letterSpacing: '0.05em' }}>
            Made in Nairobi 🇰🇪
          </p>
        </div>
      </div>
    </>
  );
}
